import React, { createContext, useContext, useState } from 'react';
import { FuturisticLoader } from '../components/common/FuturisticLoader';
import { Box } from '@mui/material';

interface LoadingContextType {
  showLoader: (message?: string, showProgress?: boolean, progress?: number) => void;
  hideLoader: () => void;
  updateProgress: (progress: number) => void;
  updateMessage: (message: string) => void;
  isLoading: boolean;
  message: string;
  progress: number;
  showProgress: boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('Initializing System');
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  const showLoader = (newMessage = 'Initializing System', newShowProgress = false, newProgress = 0) => {
    setMessage(newMessage);
    setShowProgress(newShowProgress);
    setProgress(newProgress);
    setIsLoading(true);
  };

  const hideLoader = () => {
    setIsLoading(false);
    setProgress(0);
    setShowProgress(false);
  };

  const updateProgress = (newProgress: number) => {
    setProgress(newProgress);
  };

  const updateMessage = (newMessage: string) => {
    setMessage(newMessage);
  };

  const value: LoadingContextType = {
    showLoader,
    hideLoader,
    updateProgress,
    updateMessage,
    isLoading,
    message,
    progress,
    showProgress,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <FuturisticLoader
            message={message}
            showProgress={showProgress}
            progress={progress}
            size="medium"
            enableSound={true}
          />
        </Box>
      )}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
}; 